/**
 * Global HR — Sprint 1 · Org Foundation UIs.
 *
 * Exports:
 *   - CountriesPage     (unified country master)
 *   - OrgHierarchyPage  (Company → Division → Department → Team tree)
 *   - EmployeeDocsPage  (digital file — per employee folder view)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Plus, Trash2, Pencil, Globe, Building2, X, Upload, FileText, ChevronDown, ChevronRight, Folder, Download } from 'lucide-react';
import { apiClient, API } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-black/5 rounded-2xl ${className}`}>{children}</div>
);
const H1 = ({ eyebrow, title, sub }) => (
  <div className="mb-6">
    <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">{eyebrow}</span>
    <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{title}</h1>
    {sub && <p className="mt-1 text-sm text-[#0b1a2e]/60">{sub}</p>}
  </div>
);
const Btn = ({ variant = 'primary', className = '', ...rest }) => {
  const styles = {
    primary: 'bg-[#0f5c3d] text-white hover:bg-[#0b4a30]',
    ghost: 'border border-[#0f5c3d]/20 text-[#0f5c3d] hover:bg-[#f7fbf8]',
    dark: 'bg-[#0b1a2e] text-white hover:bg-[#0f5c3d]',
    danger: 'text-red-600 hover:bg-red-50',
  }[variant];
  return <button {...rest} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${styles} ${className}`} />;
};
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>
    {children}
  </div>
);
const Input = (p) => <input {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm ${p.className || ''}`} />;
const Select = (p) => <select {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white ${p.className || ''}`}>{p.children}</select>;

// ============================================================
// COUNTRIES
// ============================================================
export const CountriesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/hr/countries');
      setItems(r.data.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const seed = async () => {
    try { const r = await apiClient.post('/hr/countries/seed-defaults'); toast({ title: 'Seeded', description: `${r.data.inserted} inserted` }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const del = async (c) => { if (window.confirm(`Delete ${c.name}?`)) { await apiClient.delete(`/hr/countries/${c.id}`); load(); } };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · Sprint 1" title="Countries master" sub="Currency, timezone, working days, statutory hints & employee-ID prefix per country." />
        <div className="flex gap-2 mb-6">
          <Btn variant="ghost" onClick={seed} data-testid="seed-countries-btn">Seed defaults</Btn>
          <Btn onClick={() => setEditing({})} data-testid="new-country-btn"><Plus className="w-3.5 h-3.5" /> New country</Btn>
        </div>
      </div>
      <Card>
        {loading ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 inline animate-spin" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                <tr><th className="text-left p-2">Code</th><th className="text-left p-2">Name</th><th className="text-left p-2">Currency</th><th className="text-left p-2">Prefix</th><th className="text-left p-2">Timezone</th><th className="text-left p-2">Working days</th><th className="text-left p-2">Notes</th><th></th></tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-black/5" data-testid={`country-row-${c.code}`}>
                    <td className="p-2 font-mono text-xs">{c.code}</td>
                    <td className="p-2"><Globe className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1.5" />{c.name}</td>
                    <td className="p-2 text-xs">{c.currency}</td>
                    <td className="p-2 text-xs font-mono">{c.code_prefix}</td>
                    <td className="p-2 text-xs text-[#0b1a2e]/70">{c.timezone}</td>
                    <td className="p-2 text-xs">{c.working_days}</td>
                    <td className="p-2 text-xs text-[#0b1a2e]/60">{c.notes}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(c)} className="text-[#0f5c3d] mr-2"><Pencil className="w-3.5 h-3.5 inline" /></button>
                      <button onClick={() => del(c)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {editing && <CountryModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const CountryModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ code: '', name: '', currency: 'USD', code_prefix: '', timezone: '', working_days: 'Mon-Fri', notes: '', active: true, ...initial });
  const save = async () => {
    try {
      const payload = { ...f, code: (f.code || '').toUpperCase(), code_prefix: (f.code_prefix || '').toUpperCase(), currency: (f.currency || '').toUpperCase() };
      if (f.id) await apiClient.patch(`/hr/countries/${f.id}`, payload);
      else await apiClient.post('/hr/countries', payload);
      toast({ title: 'Saved' });
      onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="country-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{f.id ? 'Edit' : 'New'} country</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Code (2-4)"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="c-code" /></Field>
          <Field label="Prefix (ID)"><Input value={f.code_prefix} onChange={(e) => setF({ ...f, code_prefix: e.target.value })} data-testid="c-prefix" placeholder="IN" /></Field>
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="c-name" /></Field>
          <Field label="Currency"><Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} placeholder="INR" /></Field>
          <Field label="Timezone"><Input value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} placeholder="Asia/Kolkata" /></Field>
          <Field label="Working days"><Input value={f.working_days} onChange={(e) => setF({ ...f, working_days: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Notes"><Input value={f.notes || ''} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={save} data-testid="c-save">Save</Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ORG HIERARCHY
// ============================================================
const KIND_ICON = { company: Building2, division: Building2, department: Building2, team: Building2 };

export const OrgHierarchyPage = () => {
  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState({});
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([apiClient.get('/hr/org/tree'), apiClient.get('/hr/org/units')]);
      setTree(t.data.data || []);
      setFlat(u.data.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const del = async (id) => { if (window.confirm('Delete this unit? Children will be detached.')) { await apiClient.delete(`/hr/org/units/${id}`); load(); } };
  const renderNode = (node, depth = 0) => {
    const isOpen = expanded[node.id] !== false; // default open
    return (
      <div key={node.id}>
        <div className="flex items-center gap-1 py-1.5 border-b border-black/5 hover:bg-[#f7fbf8] group" style={{ paddingLeft: depth * 24 }} data-testid={`org-node-${node.id}`}>
          {node.children?.length > 0 ? (
            <button onClick={() => toggle(node.id)} className="p-0.5">{isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</button>
          ) : <span className="w-4" />}
          <Building2 className={`w-4 h-4 ${node.kind === 'company' ? 'text-[#0f5c3d]' : node.kind === 'division' ? 'text-emerald-600' : node.kind === 'department' ? 'text-emerald-500' : 'text-emerald-400'}`} />
          <span className="text-sm font-medium text-[#0b1a2e]">{node.name}</span>
          <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{node.kind}</span>
          {node.country_code && <span className="text-[10px] font-mono text-[#0b1a2e]/50">{node.country_code}</span>}
          <span className="text-[11px] text-[#0b1a2e]/50 ml-2">{node.employee_count || 0} 👤</span>
          <div className="ml-auto opacity-0 group-hover:opacity-100 flex gap-1">
            <button onClick={() => setEditing(node)} className="p-1 text-[#0f5c3d]"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={() => setEditing({ parent_id: node.id, kind: nextKind(node.kind) })} className="p-1 text-[#0f5c3d]" title="Add child"><Plus className="w-3.5 h-3.5" /></button>
            <button onClick={() => del(node.id)} className="p-1 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        {isOpen && node.children?.map((ch) => renderNode(ch, depth + 1))}
      </div>
    );
  };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · Sprint 1" title="Org hierarchy" sub="Company → Division → Department → Team. Employees are pinned to a leaf unit." />
        <div className="flex gap-2 mb-6">
          <Btn onClick={() => setShowAdd(true)} data-testid="new-org-unit-btn"><Plus className="w-3.5 h-3.5" /> New unit</Btn>
        </div>
      </div>
      <Card>
        <div className="p-4">
          {loading ? <div className="text-center py-10"><Loader2 className="w-5 h-5 inline animate-spin" /></div> : tree.length === 0 ? (
            <div className="text-center py-10 text-sm text-[#0b1a2e]/50">No org units yet. Click "New unit" to start with your top-level company.</div>
          ) : tree.map((n) => renderNode(n))}
        </div>
      </Card>
      {(showAdd || editing) && (
        <OrgUnitModal
          initial={editing || {}}
          parents={flat}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

const nextKind = (k) => ({ company: 'division', division: 'department', department: 'team', team: 'team' }[k] || 'department');

const OrgUnitModal = ({ initial, parents, onClose, onSaved }) => {
  const [f, setF] = useState({ name: '', kind: 'department', parent_id: null, country_code: '', head_user_id: '', code: '', description: '', ...initial });
  const save = async () => {
    try {
      const payload = { ...f, parent_id: f.parent_id || null, country_code: f.country_code || null, head_user_id: f.head_user_id || null };
      if (f.id) await apiClient.patch(`/hr/org/units/${f.id}`, payload);
      else await apiClient.post('/hr/org/units', payload);
      toast({ title: 'Saved' });
      onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="org-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{f.id ? 'Edit' : 'New'} org unit</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ou-name" /></Field>
          <Field label="Kind">
            <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="ou-kind">
              <option value="company">Company</option><option value="division">Division</option><option value="department">Department</option><option value="team">Team</option>
            </Select>
          </Field>
          <Field label="Parent">
            <Select value={f.parent_id || ''} onChange={(e) => setF({ ...f, parent_id: e.target.value })}>
              <option value="">— top level —</option>
              {parents.filter((p) => p.id !== f.id).map((p) => <option key={p.id} value={p.id}>{p.name} · {p.kind}</option>)}
            </Select>
          </Field>
          <Field label="Country (ISO)"><Input value={f.country_code || ''} onChange={(e) => setF({ ...f, country_code: e.target.value.toUpperCase() })} placeholder="IN" /></Field>
          <Field label="Code (internal)"><Input value={f.code || ''} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="FIN-IN" /></Field>
          <Field label="Head user ID"><Input value={f.head_user_id || ''} onChange={(e) => setF({ ...f, head_user_id: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Description"><Input value={f.description || ''} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={save} data-testid="ou-save">Save</Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// EMPLOYEE DIGITAL FILE
// ============================================================
export const EmployeeDocsPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => { apiClient.get('/hr/employees').then((r) => setUsers(r.data.data || [])); }, []);
  return (
    <div>
      <H1 eyebrow="Global HR · Sprint 1" title="Employee digital file" sub="Per-employee folder view — contracts, IDs, visas, certificates. Backed by Emergent Object Storage." />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-4">
          <div className="p-3 border-b border-black/5 text-xs text-[#0b1a2e]/60">{users.length} employees</div>
          <div className="max-h-[65vh] overflow-y-auto">
            {users.map((u) => (
              <button key={u.user_id} onClick={() => setSelectedId(u.user_id)} data-testid={`docs-emp-${u.user_id}`}
                className={`w-full text-left p-3 border-b border-black/5 hover:bg-[#f7fbf8] ${selectedId === u.user_id ? 'bg-[#f7fbf8]' : ''}`}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold flex items-center justify-center">{(u.name || u.email || '?').charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#0b1a2e] truncate">{u.name || u.email}</div>
                    <div className="text-[11px] text-[#0b1a2e]/50 truncate">{u.employee_code || u.email}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>
        <div className="lg:col-span-8">
          {selectedId ? <EmployeeDocPanel userId={selectedId} /> : <Card className="p-16 text-center text-sm text-[#0b1a2e]/50">Select an employee to view their file.</Card>}
        </div>
      </div>
    </div>
  );
};

const EmployeeDocPanel = ({ userId }) => {
  const [grouped, setGrouped] = useState({});
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [folder, setFolder] = useState('other');
  const [title, setTitle] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [file, setFile] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/hr/employees/${userId}/documents`);
      setGrouped(r.data.grouped || {});
      setFolders(r.data.folders || []);
    } finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);
  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', folder);
      fd.append('title', title);
      if (expiresOn) fd.append('expires_on', expiresOn);
      await apiClient.post(`/hr/employees/${userId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Uploaded' });
      setFile(null); setTitle(''); setExpiresOn('');
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(false); }
  };
  const del = async (d) => { if (window.confirm(`Delete ${d.title}?`)) { await apiClient.delete(`/hr/employees/${userId}/documents/${d.id}`); load(); } };
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="text-sm font-semibold text-[#0b1a2e] mb-3 flex items-center gap-2"><Upload className="w-4 h-4 text-[#0f5c3d]" /> Upload document</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Field label="Folder">
            <Select value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="doc-folder">
              {folders.map((f) => <option key={f} value={f}>{f}</option>)}
            </Select>
          </Field>
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Passport 2028" data-testid="doc-title" /></Field>
          <Field label="Expires (optional)"><Input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} /></Field>
          <Field label="File">
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file" className="w-full text-xs border border-slate-300 rounded p-1.5" />
          </Field>
        </div>
        <div className="mt-3 flex justify-end">
          <Btn onClick={upload} disabled={!file || uploading} data-testid="doc-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
          </Btn>
        </div>
      </Card>
      {loading ? <div className="text-center py-10"><Loader2 className="w-5 h-5 inline animate-spin" /></div> :
        folders.map((f) => (
          <Card key={f}>
            <div className="p-3 border-b border-black/5 flex items-center gap-2 text-sm font-semibold text-[#0b1a2e]">
              <Folder className="w-4 h-4 text-[#0f5c3d]" /> {f.charAt(0).toUpperCase() + f.slice(1)}
              <span className="text-[11px] text-[#0b1a2e]/50">{(grouped[f] || []).length}</span>
            </div>
            {(grouped[f] || []).length === 0 ? <div className="p-4 text-xs text-[#0b1a2e]/40">No files in this folder.</div> : (
              <table className="w-full text-sm">
                <tbody>
                  {(grouped[f] || []).map((d) => (
                    <tr key={d.id} className="border-t border-black/5" data-testid={`doc-row-${d.id}`}>
                      <td className="p-2"><FileText className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1.5" />{d.title}</td>
                      <td className="p-2 text-xs text-[#0b1a2e]/60">{d.filename}</td>
                      <td className="p-2 text-xs">{d.expires_on ? `Expires ${d.expires_on}` : ''}</td>
                      <td className="p-2 text-xs text-[#0b1a2e]/40">{(d.size / 1024).toFixed(1)} KB</td>
                      <td className="p-2 text-right">
                        <a href={`${API}${d.url}`} target="_blank" rel="noreferrer" className="text-[#0f5c3d] mr-2"><Download className="w-3.5 h-3.5 inline" /></a>
                        <button onClick={() => del(d)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        ))}
    </div>
  );
};
